import { create } from "zustand";

interface WalletStore {
  isConnected: boolean;
  address: `0x${string}` | undefined;
  ready: boolean;
  // True once the full wagmi config (with WalletConnect/Reown connectors)
  // has mounted and rehydrated from the cookie. Until then, isConnected
  // may briefly reflect the minimal config's view, which doesn't know
  // about non-injected wallets — never trust the unconnected state during
  // this window for UI decisions.
  fullConfigReady: boolean;
  /**
   * True when the full wagmi config could NOT be loaded (the dynamic import
   * rejected — offline, a bad deploy, a blocked chunk). `fullConfigReady` then
   * stays false forever, so anything that WAITS on it would hang indefinitely.
   *
   * This is deliberately a separate flag rather than forcing `fullConfigReady`
   * true: the full connectors genuinely never mounted, so claiming otherwise
   * would make consumers trust a wallet state that was never rehydrated. Read
   * it as "stop waiting", not "the wallet is ready".
   */
  fullConfigFailed: boolean;
  open: () => Promise<void>;
  disconnect: () => void;
  // Internal setters used only by WalletLayer
  _setConnected: (val: boolean) => void;
  _setAddress: (val: `0x${string}` | undefined) => void;
  _setReady: (val: boolean) => void;
  _setFullConfigReady: (val: boolean) => void;
  _setFullConfigFailed: (val: boolean) => void;
  _setOpen: (fn: () => Promise<void>) => void;
  _setDisconnect: (fn: () => void) => void;
}

export const useWallet = create<WalletStore>()((set) => ({
  isConnected: false,
  address: undefined,
  ready: false,
  fullConfigReady: false,
  fullConfigFailed: false,
  open: async () => {},
  disconnect: () => {},

  _setConnected: (val) => set({ isConnected: val }),
  _setAddress: (val) => set({ address: val }),
  _setReady: (val) => set({ ready: val }),
  _setFullConfigReady: (val) => set({ fullConfigReady: val }),
  _setFullConfigFailed: (val) => set({ fullConfigFailed: val }),
  _setOpen: (fn) => set({ open: fn }),
  _setDisconnect: (fn) => set({ disconnect: fn }),
}));
